import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { brandArticles } from "@/mocks/brandArticles";

export default function ArticleNavbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background-50/95 backdrop-blur-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 font-heading font-bold text-xl whitespace-nowrap cursor-pointer"
            onClick={() => setMobileOpen(false)}
          >
            <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg text-sm font-bold">
              K
            </span>
            Kakobuy Finds Wiki
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-950 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Kategorie
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/70 rounded-xl w-[720px] p-4 grid grid-cols-4 gap-2 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors group whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
            aria-label="Otwórz menu"
          >
            <i className="ri-menu-line text-2xl"></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-[380px] bg-background-50 overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <Link
                to="/"
                className="flex items-center gap-2 text-foreground-950 font-heading font-bold text-lg whitespace-nowrap cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-7 h-7 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg text-xs font-bold">
                  K
                </span>
                Kakobuy Finds Wiki
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                aria-label="Zamknij menu"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-4">
              {/* CTA Buttons */}
              <div className="flex flex-col gap-2 mb-6">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  Kakobuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 border border-foreground-200 text-foreground-700 rounded-full text-sm font-semibold hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  Buy It Now
                  <i className="ri-shopping-bag-line"></i>
                </a>
              </div>

              {/* Categories */}
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                Kategorie Produktów
              </h3>
              <div className="grid grid-cols-2 gap-1.5 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200 pt-5">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                  Przewodniki po Arkuszach
                </h3>
                <div className="flex flex-col gap-1">
                  <Link
                    to="/kakobuy-sheet-przewodnik"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-sm text-foreground-700 hover:text-foreground-950"
                  >
                    <i className="ri-file-text-line text-primary-500"></i>
                    Kakobuy Spreadsheet Przewodnik
                  </Link>
                  {brandArticles.map((ba) => (
                    <Link
                      key={ba.slug}
                      to={`/${ba.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-sm text-foreground-700 hover:text-foreground-950"
                    >
                      <i className="ri-arrow-right-line text-foreground-400"></i>
                      {ba.brandName} Spreadsheet
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-background-200 mt-2">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-foreground-500 hover:text-foreground-950 transition-colors text-sm cursor-pointer py-1"
              >
                <i className="ri-home-4-line"></i>
                Strona Główna
              </Link>
            </div>

            <div className="p-4 border-t border-background-200">
              <p className="text-xs text-foreground-400">
                © 2026 Kakobuy Finds Wiki
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}